import { useEffect, useState } from "react";
import { MessageCircle, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { href: "#how", label: "How it works" },
  { href: "#courses", label: "Courses" },
  { href: "#pricing", label: "Pricing" },
  { href: "#teacher", label: "Teacher" },
  { href: "#faq", label: "FAQ" },
];

export default function Nav({ onJoinClick, whatsappNumber = "" }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const wa = whatsappNumber || "447000000000";

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-40 transition-all ${
        scrolled
          ? "backdrop-blur-xl bg-[hsl(40_33%_98%)]/85 border-b border-border/60"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-2"
        >
          <div className="grid h-9 w-9 place-items-center rounded-full bg-primary text-primary-foreground font-serif text-lg">
            A
          </div>
          <div className="leading-tight">
            <div className="font-serif text-lg text-primary">AfroLingo</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              African Language Academy
            </div>
          </div>
        </a>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              className="text-sm text-foreground/80 transition-colors hover:text-primary"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <a
            href={`https://wa.me/${wa.replace(/[^0-9]/g, "")}`}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp"
            className="grid h-10 w-10 place-items-center rounded-full bg-[hsl(148_50%_35%)] text-white transition-transform hover:-translate-y-0.5"
            aria-label="WhatsApp"
          >
            <MessageCircle className="h-5 w-5" strokeWidth={1.75} />
          </a>
          <Button
            onClick={onJoinClick}
            data-testid="nav-join-btn"
            className="rounded-full bg-[hsl(41_85%_55%)] px-6 py-5 text-sm font-medium text-[hsl(150_20%_15%)] shadow-sm hover:bg-[hsl(41_85%_50%)]"
          >
            Join Waiting List
          </Button>
        </div>

        <button
          className="md:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[hsl(40_33%_98%)] border-t border-border">
          <div className="mx-auto max-w-7xl px-4 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-2 text-base"
              >
                {l.label}
              </a>
            ))}
            <Button
              onClick={() => {
                setOpen(false);
                onJoinClick?.();
              }}
              data-testid="nav-join-btn-mobile"
              className="rounded-full bg-[hsl(41_85%_55%)] py-5 font-medium text-[hsl(150_20%_15%)] hover:bg-[hsl(41_85%_50%)]"
            >
              Join Waiting List
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
